import {
  SortingState,
  TableColumnWidthInfo,
  Sorting,
  DataTypeProvider,
  IntegratedSorting,
  Column,
  SearchState,
  IntegratedFiltering,
  PagingState,
  IntegratedPaging,
} from '@devexpress/dx-react-grid'
import {
  Grid,
  PagingPanel,
  SearchPanel,
  Table,
  TableColumnResizing,
  TableHeaderRow,
  Toolbar,
} from '@devexpress/dx-react-grid-bootstrap4'
import {
  DndContext,
  closestCenter,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import SortableItem from 'components/SortableItem'
import ParentSortableTableCell from 'components/SortableTableCell'
import useReorder from 'hooks/useReorder'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import SortableOverLayContent from 'components/SortableOverlayContent'
import { ComponentType, useMemo, useState } from 'react'
import reorderRows from 'utils/reorderRows'
import useInit from './hooks/useInit'
import Entity from '../types/Entity'
import { Form } from 'react-bootstrap'
import handleToggleActiveParent from '../utils/handleToggleActive'
import useInitColumns from '../hooks/useInitColumns'

interface TableIncludedRowsProps {
  rows: Array<Entity>
  setRows: Function
  relationalFieldName: string
  toggleActiveUrl?: string
  handleExcludeRow: (row: Entity) => void
  sortRowsUrl?: string
  disableActions: boolean
  setDisableActions: Function
  handleModalRowEdit?: (row: Entity) => void
  showSearch: boolean
  showPagination: boolean
}

const TableIncludedRows: React.FunctionComponent<TableIncludedRowsProps> = (
  props: TableIncludedRowsProps,
) => {
  const [dragEndEvent, setDragEndEvent] = useState<any | null>(null)

  const rowIds = useMemo(() => props.rows?.map(({ id }) => id), [props.rows])

  const [columns, setColumns] = useState<Array<Column>>([])

  const [defaultSorting, setDefaultSorting] = useState<Array<Sorting>>([])

  const [columnWidths, setColumnWidths] = useState<TableColumnWidthInfo[]>([])

  const handleToggleActive = (row: Entity) => {
    return handleToggleActiveParent({
      row: row,
      relationalFieldName: props.relationalFieldName,
      url: props.toggleActiveUrl ?? '',
      rows: props.rows,
      setRows: props.setRows,
      setDisableActions: props.setDisableActions,
    })
  }

  const [activeFormatter] = useState<any>({
    for: ['active'],
    formatterComponent: (props) => {
      return (
        <Form.Check
          type='switch'
          onChange={() => {
            handleToggleActive(props.row)
          }}
          disabled={props.disableActions}
          checked={props.value}
        />
      )
    },
    editorComponent: undefined,
    availableFilterOperations: undefined,
  })

   const [nameFormatter] = useState<any>({
    for: ['name'],
    formatterComponent: (props) => {
      if (props.row?.isRecent === true) {
        return <span className='recent-filter-value mx-8'>{props.row.name}</span>
      }
      return props.row.name
    },
    editorComponent: undefined,
    availableFilterOperations: undefined,
  })

  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}))

  const SortableTableRow: ComponentType<any> | undefined = ({ row, ...restProps }) => {
    return (
      <SortableItem
        key={props.rows.indexOf(row)}
        index={props.rows.indexOf(row)}
        row={row}
        {...restProps}
      />
    )
  }

  const SortableTableCell: any = ({ value, ...rest }) => {
    return (
      <ParentSortableTableCell
        value={value}
        tableCellProps={rest}
        draggableColumnName='sortColumn'
      />
    )
  }

  useInit({
    rows: props.rows,
    handleExcludeRow: props.handleExcludeRow,
    disableActions: props.disableActions,
    handleModalRowEdit: props.handleModalRowEdit,
  })

  useInitColumns({
    setColumns,
    setColumnWidths,
    setDefaultSorting,
    toggleActiveUrl: props.toggleActiveUrl,
    sortRowsUrl: props.sortRowsUrl,
  })

  useReorder({
    rows: props.rows,
    dragEndEvent,
    setRows: props.setRows,
    sortRowsUrl: props.sortRowsUrl ?? '#',
    reorderRows,
  })

  const PagingContainer = (props) => {
    return <PagingPanel.Container {...props} className='table-footer' />
  }

  const SearchPanelInput = (props) => {
      return  <SearchPanel.Input {...props} className='w-100 inline-space-r search-input' />
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        onDragEnd={(e) => setDragEndEvent(e)}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
          <Grid rows={props.rows} columns={columns}>
            <DataTypeProvider {...nameFormatter} />
            <DataTypeProvider {...activeFormatter} />
            <SortingState defaultSorting={defaultSorting} />
            {props.showSearch && <SearchState defaultValue='' />}
            {props.showSearch && <IntegratedFiltering /> }
            {props.showPagination && <PagingState defaultCurrentPage={0} pageSize={15} />}
            {props.sortRowsUrl === undefined && <IntegratedSorting />}
            {props.showPagination && <IntegratedPaging />}
            {props.showSearch && <Toolbar />}
            {props.showSearch && <SearchPanel inputComponent={SearchPanelInput} />}
            <Table
              cellComponent={SortableTableCell}
              rowComponent={props.sortRowsUrl === undefined ? Table.Row : SortableTableRow}
            />
            <TableColumnResizing
              defaultColumnWidths={columnWidths}
              columnWidths={columnWidths}
              onColumnWidthsChange={(nextColumnWidths: Array<TableColumnWidthInfo>) => {
                setColumnWidths(nextColumnWidths)
              }}
              resizingMode='nextColumn'
            />
            <TableHeaderRow />
            {props.showPagination && <PagingPanel pageSizes={[]} containerComponent={PagingContainer} />}
          </Grid>
        </SortableContext>
        <DragOverlay>
          <SortableOverLayContent />
        </DragOverlay>
      </DndContext>
    </>
  )
}

export default TableIncludedRows
